import { computed } from '@ember/object';
import { or, readOnly } from '@ember/object/computed';
import { attr, hasMany, belongsTo } from '@ember-data/model';
import { convertToUTCDate } from 'sh-www/utils/date-and-time';
import ShModel from './sh-model';

const CREATED_FROM = {
  CREATE: 'create',
  DIRECT: 'direct',
  DIY: 'diy',
  PITCH: 'pitch',
  STANDARD: 'standard'
};

const TYPES = {
  STANDARD: 1,
  PITCH: 2,
};

const DIRECT = {
  NONE: 0,
  PROJECT: 1,
  PROJECT_WORK_COMPLETE: 2,
};

const PRIVACY_LEVEL = {
  PUBLIC: 1,
  NETWORK: 2,
  PRIVATE: 3,
  GROUP: 4,
};

const FORMATS = {
  ALL: 'all',
  ADMIN: 'admin',
  DEFAULT: 'default',
  SELLER_PAGINATED: 'seller-paginated',
  DASHBOARD: 'dashboard',
  RECOMMENDED: 'recommended',
  OTHER: 'other',
  SAVED: 'saved',
  SEARCH: 'search',
}

const PRIVACY_LEVEL_DESCRIPTIONS = {
  [PRIVACY_LEVEL.NETWORK]: {
    text: 'Only creators in your network will be able to discover your posting.',
  },
  [PRIVACY_LEVEL.PRIVATE]: {
    text: 'Creators won’t be able to discover your posting unless you manually invite them.',
  }
}

const RATE_TYPE = {
  FLAT: 1,
  DAY: 2,
};

const STATUS = {
  CLOSED: 'closed',
  DRAFT: 'draft',
  LIVE: 'live',
  IN_REVIEW: 'in_review',
};

export const ASSIGNMENT = {
  CREATED_FROM,
  FORMATS,
  TYPES,
  DIRECT,
  PRIVACY_LEVEL,
  PRIVACY_LEVEL_DESCRIPTIONS,
  RATE_TYPE,
  STATUS,
};

const Assignment = ShModel.extend({
  activeApplicationCount: attr('number'),
  compensationMax: attr('number'),
  compensationMin: attr('number'),
  completedApplicationCount: attr('number'),
  createdAt: attr('date'),
  createdFrom: attr('string'),
  deadline: attr('date'),
  declinedApplicationCount: attr('number'),
  description: attr('string'),
  descriptionLinkified: attr('string'),
  estimatedDays: attr('number'),
  examples: attr('string'),
  examplesLinkified: attr('string'),
  expediteCommitment: attr('boolean'),
  freelancerMatch: attr('string'),
  isAllowsExpenses: attr('boolean-nullable'),
  isDirect: attr('boolean'),
  isExpired: attr('boolean'),
  isInvited: attr('boolean'),
  isHomeLocation: attr('boolean'),
  isLive: attr('boolean'),
  isRecentLocation: attr('boolean'),
  isRecommended: attr('boolean'),
  isUrgent: attr('boolean'),
  lastActivity: attr('date'),
  maxExpectedPayoutDays: attr('number'),
  minRating: attr('number'),
  pendingApplicationCount: attr('number'),
  privacyLevel: attr('number'),
  publishedAt: attr('date'),
  rateType: attr('number'),
  select: attr('boolean'),
  sellerFeesCovered: attr('boolean'),
  status: attr('string'),
  slug: attr('string'),
  title: attr('string'),
  type: attr('number'),
  updatedAt: attr('date'),
  wasBlasted: attr('boolean'),
  allow: attr(),

  //relationships
  actors: hasMany('user', { async: false }),
  applications: hasMany('application', { async: false }),
  assignmentQuestions: hasMany('assignment-question', { async: false }),
  assignmentDeliverables: hasMany('assignment-deliverable', { async: false }),
  assignmentSchedules: hasMany('assignment-schedule', { async: false }),
  assignmentUserMeta: belongsTo('assignment-user-metum', { async: false }),
  assignmentUserMetas: hasMany('assignment-user-metum', { async: false }),
  assignmentStatistics: belongsTo('assignment-statistic', { async: false }),
  assignmentInvites: hasMany('assignmentInvite', { async: false }),
  blasts: hasMany('blast', { async: false }),
  brand: belongsTo('brand', { async: false }),
  buyer: belongsTo('user', { async: false }),
  candidateLocation: belongsTo('location', { async: false }),
  classifications: hasMany('classification', { async: false }),
  company: belongsTo('company', { async: false }),
  companyTeam: belongsTo('companyTeam', { async: false }),
  followers: hasMany('user', { async: false }),
  location: belongsTo('location', { async: false }),
  preAssignment: belongsTo('preAssignment', { async: false }),
  production: belongsTo('production', { async: false }),
  services: hasMany('service', { async: false }),

  //computed props
  isDraft: computed('status', function () {
    return this.status === STATUS.DRAFT;
  }),

  /** @type {Boolean} Whether the posting was created as Select outside the Create experience. */
  createdAsSelectFromPitchOrStandard: computed('createdFrom', 'select', function () {
    if (!this.get('select')) return false;

    return ['pitch', 'standard'].includes(this.get('createdFrom'));
  }),

  /**
   * Total compensation range for the assignment taking into account the rateType
   *
   * @type {String}
   */
  totalCompensationRange: computed('rateType', 'compensationMin', 'compensationMax', 'estimatedDays', function() {
    const estimatedDays = this.rateType === ASSIGNMENT.RATE_TYPE.FLAT ? 1 : this.estimatedDays;

    const totalCompensationMin = this.compensationMin * estimatedDays;
    const totalCompensationMax = this.compensationMax * estimatedDays;
    if (totalCompensationMin === totalCompensationMax) return `$${totalCompensationMin}`;
    return `$${totalCompensationMin} - $${totalCompensationMax}`;
  }),

  /** @type {string} */
  candidateLocationText: computed('candidateLocation.formattedAddress', function() {
    const { candidateLocation } = this;

    if (candidateLocation) {
      return candidateLocation.formattedAddress;
    }

    return 'Global';
  }),

  /**
   * Human readable cost text of the assignment.
   *
   * @type {String}
   */
  costText: computed(
  'compensationMax',
  'compensationMin',
  'estimatedDays',
  'isAllowsExpenses',
  'isDirect',
  'rateType',
  function() {
    let estimatedDays = this.get('estimatedDays'),
      rateTypeText = this.get('rateType') === ASSIGNMENT.RATE_TYPE.DAY ?
        `day rate • ${estimatedDays} ${estimatedDays === 1 ? 'day' : 'days'}` :
        'flat fee';
    let expensesString = this.get('isAllowsExpenses') ? 'Expenses' : 'No expenses';

    if (this.get('isDirect')) {
      return `Direct hire • ${expensesString}`;
    }

    return `$${this.get('compensationMin')} - $${this.get('compensationMax')} ${rateTypeText} • ${expensesString}`;
  }),

  /**
   * This will display the deadline and ensure we display in the UTC format.
   *
   * @type {Moment}
   */
  deadlineUTC: computed('deadline', function() {
    let deadline = this.get('deadline');
    if (!deadline) {
      return null;
    }

    return convertToUTCDate(deadline);
  }),

  /**
  * @deprecated Use slug for URL and id for API calls
  */
  idWithSlug: readOnly('slug'),

  /** @type {string} */
  locationText: computed('location.formattedAddress', function() {
    const { location } = this;

    if (location) {
      return location.formattedAddress;
    }

    return 'Global';
  }),

  /**
   * In almost all cases you should be using this title to display to the user.
   *
   * @type {String}     Title taking into account the urgent flag.
   */
  titleWithUrgent: computed('title', 'isUrgent', function() {
    if (this.get('isUrgent')) {
      return `[Urgent] ${this.get('title')}`;
    }

    return this.get('title');
  }),

  /**
   * Only allow sellers to submit feedback on an assignment if it was recommended, blasted, or they were invited.
   *
   * @type {Boolean}
   */
  allowSellerFeedback: or('isInvited', 'wasBlasted', 'isRecommended'),

  scheduleItems: computed('assignmentSchedules.@each.date', function() {
    return this.assignmentSchedules.sortBy('date');
  }),
});

export default Assignment;
